import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Box, Card, CardContent, Typography, Button, Grid, Tab, Tabs,
  Avatar, Table, TableBody, TableCell, TableContainer, TableRow,
  TableHead, Paper, Chip, Switch, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select,
  FormControl, InputLabel
} from '@mui/material';
import {
  Security as SecurityIcon, PersonAdd as PersonAddIcon,
  Delete as DeleteIcon, Shield as ShieldIcon,
  AdminPanelSettings as SuperIcon, Work as ManagerIcon,
  ContactPhone as TeleIcon, VerifiedUser as ReviewerIcon,
  FiberManualRecord as ActiveIcon
} from '@mui/icons-material';

const ROLE_DETAILS = [
  {
    name: 'Super Administrator',
    key: 'superadmin',
    icon: <SuperIcon sx={{ color: '#ef4444' }} />,
    color: '#ef4444',
    bgColor: 'rgba(239, 68, 68, 0.08)',
    desc: 'Full absolute system access. Can modify system settings, access credentials, and database schemas.',
    permissions: ['all_access', 'manage_settings', 'modify_users', 'audit_logs', 'financials']
  },
  {
    name: 'Administrator',
    key: 'admin',
    icon: <SuperIcon sx={{ color: '#f59e0b' }} />,
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.08)',
    desc: 'Comprehensive access to all platform actions, database records, and operational configurations.',
    permissions: ['manage_users', 'manage_challans', 'scholarship_verification', 'telemarketing_access', 'audit_logs']
  },
  {
    name: 'Telemarketing Agent',
    key: 'telemarketing',
    icon: <TeleIcon sx={{ color: '#10b981' }} />,
    color: '#10b981',
    bgColor: 'rgba(16, 185, 129, 0.08)',
    desc: 'Access to calling leads. Can view contact info, change lead status, and log conversation notes.',
    permissions: ['telemarketing_access', 'view_leads', 'update_lead_status', 'add_call_notes']
  },
  {
    name: 'Scholarship Reviewer',
    key: 'reviewer',
    icon: <ReviewerIcon sx={{ color: '#3b82f6' }} />,
    color: '#3b82f6',
    bgColor: 'rgba(59, 130, 246, 0.08)',
    desc: 'Responsible for checking eligibility criteria, approving/rejecting scholarship card requests.',
    permissions: ['scholarship_verification', 'view_student_records']
  }
];

const UsersAndRoles = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin-users');
      if (res.data.success) {
        setUsers(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Add User Dialog State
  const [openAdd, setOpenAdd] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    name: '',
    email: '',
    role: 'telemarketing',
    password: ''
  });

  const handleTabChange = (_: any, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleStatusToggle = async (userId: string, currentStatus: boolean) => {
    try {
      await api.put(`/admin-users/${userId}`, { isActive: !currentStatus });
      fetchUsers();
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to remove this staff user from the system?')) {
      try {
        await api.delete(`/admin-users/${userId}`);
        fetchUsers();
      } catch (err) {
        console.error("Failed to delete user", err);
      }
    }
  };

  const handleAddSubmit = async () => {
    if (!newUser.username || !newUser.name || !newUser.email || !newUser.password) {
      alert('Please fill out all required fields.');
      return;
    }
    
    try {
      await api.post('/admin-users', {
        username: newUser.username.toLowerCase(),
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role
      });
      fetchUsers();
      setOpenAdd(false);
      setNewUser({ username: '', name: '', email: '', role: 'telemarketing', password: '' });
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add user');
    }
  };

  // Stats
  const activeCount = users.filter(u => u.isActive).length;
  const adminCount = users.filter(u => u.role === 'admin' || u.role === 'superadmin').length;

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3.5} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center">
          <Avatar sx={{ bgcolor: 'rgba(5,150,105,0.1)', color: '#059669', width: 48, height: 48, mr: 2 }}>
            <SecurityIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '1.45rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.1 }}>Users & Roles Management</Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 400, mt: 0.3 }}>
              Audit staff log profiles, assign system access roles, and manage permissions.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setOpenAdd(true)}
          sx={{
            borderRadius: '12px',
            bgcolor: '#059669',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            px: 2.5,
            height: '42px',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#047857', boxShadow: 'none' }
          }}
        >
          Add Internal User
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', bg: 'linear-gradient(90deg, #059669, #10b981)' }} />
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Internal Staff</Typography>
              <Typography sx={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', mb: 0.5, mt: 1 }}>{users.length} Profiles</Typography>
              <Typography sx={{ fontSize: '0.73rem', color: '#64748b', fontWeight: 500 }}>Active in database registry</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', bg: 'linear-gradient(90deg, #3b82f6, #60a5fa)' }} />
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Connections</Typography>
              <Typography sx={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', mb: 0.5, mt: 1 }}>{activeCount} Online</Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ActiveIcon sx={{ color: '#10b981', fontSize: 10 }} />
                <Typography sx={{ fontSize: '0.73rem', color: '#10b981', fontWeight: 600 }}>Active operational status</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', bg: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Administrators</Typography>
              <Typography sx={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', mb: 0.5, mt: 1 }}>{adminCount} Users</Typography>
              <Typography sx={{ fontSize: '0.73rem', color: '#f59e0b', fontWeight: 600 }}>Full-system administrative access</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', bg: 'linear-gradient(90deg, #8b5cf6, #a78bfa)' }} />
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Defined Security Roles</Typography>
              <Typography sx={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', mb: 0.5, mt: 1 }}>{ROLE_DETAILS.length} Roles</Typography>
              <Typography sx={{ fontSize: '0.73rem', color: '#8b5cf6', fontWeight: 600 }}>Custom role access control list</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs Menu */}
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{
          mb: 3.5,
          borderBottom: '1px solid #e2e8f0',
          '& .MuiTabs-indicator': { bgcolor: '#059669', height: '3px' },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: '#64748b',
            '&.Mui-selected': { color: '#059669' }
          }
        }}
      >
        <Tab icon={<ShieldIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Internal Users & Staff" />
        <Tab icon={<SecurityIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Roles & Permissions Matrix" />
      </Tabs>

      {/* TAB 1: INTERNAL STAFF USERS TABLE */}
      {tabIndex === 0 && (
        <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)' }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none', borderRadius: '14px', overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', py: 2 }}>Staff User</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', py: 2 }}>Email Address</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', py: 2 }}>Security Role</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', py: 2 }}>Last Active</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', py: 2 }}>Account Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', py: 2 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const roleObj = ROLE_DETAILS.find(r => r.key === user.role);
                  return (
                    <TableRow key={user.id} hover>
                      {/* Staff User Avatar & Username */}
                      <TableCell sx={{ py: 2 }}>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ bgcolor: roleObj?.bgColor || 'rgba(5,150,105,0.08)', color: roleObj?.color || '#059669', mr: 1.8, width: 38, height: 38 }}>
                            {(user.name || user.username).charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.88rem' }}>{user.name || user.username}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>@{user.username}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Email */}
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#334155', fontWeight: 500 }}>{user.email}</Typography>
                      </TableCell>

                      {/* Role Badge */}
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          icon={roleObj?.icon}
                          label={roleObj?.name || user.role}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            color: roleObj?.color || '#3b82f6',
                            bgcolor: roleObj?.bgColor || 'rgba(59,130,246,0.08)',
                            borderRadius: '8px',
                            border: 'none',
                            '& .MuiChip-icon': { fontSize: 14 }
                          }}
                        />
                      </TableCell>

                      {/* Last Active */}
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>{user.lastActive}</Typography>
                      </TableCell>

                      {/* Status Toggle */}
                      <TableCell sx={{ py: 2 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Switch
                            checked={user.isActive}
                            onChange={() => handleStatusToggle(user._id, user.isActive)}
                            color="success"
                            size="small"
                          />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: user.isActive ? '#059669' : '#94a3b8' }}>
                            {user.isActive ? 'ACTIVE' : 'SUSPENDED'}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Delete Action */}
                      <TableCell sx={{ py: 2 }} align="right">
                        <IconButton
                          onClick={() => handleDeleteUser(user._id)}
                          disabled={user.username === 'admin' || user.username === 'superadmin'}
                          size="small"
                          sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.06)' } }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* TAB 2: ROLES & PERMISSIONS CARDS */}
      {tabIndex === 1 && (
        <Grid container spacing={3}>
          {ROLE_DETAILS.map((role) => (
            <Grid size={{ xs: 12, md: 6 }} key={role.key}>
              <Card sx={{ borderRadius: '14px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,.04)', height: '100%' }}>
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <Avatar sx={{ bgcolor: role.bgColor, color: role.color, width: 44, height: 44 }}>
                      {role.icon}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{role.name}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: role.color, fontWeight: 700, textTransform: 'uppercase' }}>Security Domain: {role.key}</Typography>
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mb: 2.5, flexGrow: 1, lineHeight: 1.5 }}>
                    {role.desc}
                  </Typography>

                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', mb: 1.2 }}>Assigned Permission Tags</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {role.permissions.map((perm, idx) => (
                      <Chip
                        key={idx}
                        label={perm.replace(/_/g, ' ')}
                        sx={{
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          bgcolor: '#f1f5f9',
                          color: '#475569',
                          borderRadius: '6px',
                          border: 'none'
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add User Dialog */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1.5, fontFamily: "'Plus Jakarta Sans',sans-serif" }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.15rem', pb: 1 }}>
          Add New Operational User
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Box display="flex" flexDirection="column" gap={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Full Name"
              required
              fullWidth
              size="small"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Username"
              required
              fullWidth
              size="small"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Email Address"
              required
              fullWidth
              size="small"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="role-select-label" shrink>Security Access Role</InputLabel>
              <Select
                labelId="role-select-label"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                label="Security Access Role"
                displayEmpty
              >
                <MenuItem value="superadmin">Super Administrator</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
                <MenuItem value="telemarketing">Telemarketing Agent</MenuItem>
                <MenuItem value="reviewer">Scholarship Reviewer</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Initial Password"
              required
              fullWidth
              size="small"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenAdd(false)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSubmit}
            variant="contained"
            sx={{
              borderRadius: '10px',
              bgcolor: '#059669',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#047857', boxShadow: 'none' }
            }}
          >
            Create User Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersAndRoles;
